import * as React from "react";
import { useState, useEffect, useRef, useCallback, useMemo } from "react";
import { Locate, MapPin } from "lucide-react";
import {
  Location,
  useLocationSearchByCoordinatesQuery,
  useLocationSearchQuery,
} from "../../api/locationApi.ts";
import { toast } from "react-hot-toast";
import { Trans, useLingui } from "@lingui/react/macro";

interface LocationSearchProps {
  onLocationSelect: (location: Location) => void;
  initialValue?: string;
}

const LocationSearch: React.FC<LocationSearchProps> = ({
  onLocationSelect,
  initialValue = "",
}) => {
  const [locationQuery, setLocationQuery] = useState<string>(initialValue);
  const [debouncedQuery, setDebouncedQuery] = useState<string>("");
  const [showLocationDropdown, setShowLocationDropdown] =
    useState<boolean>(false);
  const [selectedIndex, setSelectedIndex] = useState<number>(-1);
  const [longitude, setLongitude] = useState<number | undefined>(undefined);
  const [latitude, setLatitude] = useState<number | undefined>(undefined);
  const [locationLoading, setLocationLoading] = useState(false);
  const { t } = useLingui();

  const inputRef = useRef<HTMLInputElement>(null);
  const dropdownRef = useRef<HTMLDivElement>(null);

  // Helper function to format the address for display in the dropdown
  function formatLocationForDisplay(location: Location) {
    const streetAndNumber = [location.streetName, location.houseNumber]
      .filter(Boolean)
      .join(" ");
    const cityAndPostalCode = [location.postalCode, location.city]
      .filter(Boolean)
      .join(" ");

    let mainLine = t`Unknown Location`;
    let secondaryLine = "";

    if (streetAndNumber) {
      mainLine = streetAndNumber;
      secondaryLine = [cityAndPostalCode, location.countryCode]
        .filter(Boolean)
        .join(", ");
    } else if (cityAndPostalCode) {
      mainLine = cityAndPostalCode;
      secondaryLine = location.countryCode || "";
    } else if (location.countryCode) {
      mainLine = location.countryCode;
    }

    return { mainLine, secondaryLine };
  }

  // Sync with initialValue when it changes
  useEffect(() => {
    setLocationQuery(initialValue);
  }, [initialValue]);

  // Debounce the search query
  useEffect(() => {
    const timer = setTimeout(() => {
      setDebouncedQuery(locationQuery);
    }, 500);

    return () => {
      clearTimeout(timer);
    };
  }, [locationQuery]);

  const { data: locationResults, isLoading } =
    useLocationSearchQuery(debouncedQuery);

  const {
    data: locationByCoordinatesResults,
    isLoading: locationByCoordinatesLoading,
  } = useLocationSearchByCoordinatesQuery(longitude, latitude);

  const locationLookupLoading = useMemo(
    () => locationLoading || locationByCoordinatesLoading,
    [locationLoading, locationByCoordinatesLoading],
  );

  // Reset selected index when results change
  useEffect(() => {
    setSelectedIndex(-1);
  }, [locationResults]);

  // Scroll selected item into view
  useEffect(() => {
    if (selectedIndex >= 0 && dropdownRef.current) {
      const selectedElement = dropdownRef.current.children[
        selectedIndex
      ] as HTMLElement;
      if (selectedElement) {
        selectedElement.scrollIntoView({
          block: "nearest",
          behavior: "smooth",
        });
      }
    }
  }, [selectedIndex]);

  function buildLocationString(location: Location) {
    return [
      `${location.streetName || ""} ${location.houseNumber || ""}`.trim(),
      `${location.postalCode || ""} ${location.city || ""}`.trim(),
      location.countryCode,
    ]
      .filter(Boolean)
      .join(", ");
  }

  const handleLocationSelect = useCallback(
    (location: Location) => {
      if (location.latitude && location.longitude) {
        const displayText = buildLocationString(location);
        setLocationQuery(displayText);
        setShowLocationDropdown(false);
        setSelectedIndex(-1);
        onLocationSelect(location);
      }
    },
    [onLocationSelect],
  );

  const handleKeyDown = (e: React.KeyboardEvent<HTMLInputElement>) => {
    if (
      !showLocationDropdown ||
      !locationResults ||
      locationResults.length === 0
    ) {
      return;
    }

    switch (e.key) {
      case "ArrowDown":
        e.preventDefault();
        setSelectedIndex((prev) =>
          prev < locationResults.length - 1 ? prev + 1 : prev,
        );
        break;
      case "ArrowUp":
        e.preventDefault();
        setSelectedIndex((prev) => (prev > 0 ? prev - 1 : -1));
        break;
      case "Enter":
        e.preventDefault();
        if (selectedIndex >= 0 && selectedIndex < locationResults.length) {
          handleLocationSelect(locationResults[selectedIndex]);
        }
        break;
      case "Escape":
        e.preventDefault();
        setShowLocationDropdown(false);
        setSelectedIndex(-1);
        break;
    }
  };

  useEffect(() => {
    if (!locationLookupLoading && locationByCoordinatesResults) {
      if (locationByCoordinatesResults.length > 0) {
        handleLocationSelect(locationByCoordinatesResults[0]);
      } else {
        toast.error(
          t`Failed to load location. Please search your address manually.`,
        );
      }
    }
  }, [
    handleLocationSelect,
    locationByCoordinatesResults,
    locationLookupLoading,
  ]);

  const loadLocationFromDevice = () => {
    setLocationLoading(true);
    navigator.geolocation.getCurrentPosition(
      (position) => {
        setLongitude(position.coords.longitude);
        setLatitude(position.coords.latitude);
        setLocationLoading(false);
      },
      (error) => {
        setLocationLoading(false);
        console.log(error);
        toast.error(
          t`Loading your location failed. Please try again or enter your address manually.`,
        );
      },
    );
  };

  return (
    <div className="relative">
      <div className="flex space-x-2">
        <div className="relative flex-1">
          <input
            ref={inputRef}
            type="text"
            value={locationQuery}
            onChange={(e) => {
              setLocationQuery(e.target.value);
              setShowLocationDropdown(true);
            }}
            onFocus={() => setShowLocationDropdown(true)}
            onKeyDown={handleKeyDown}
            placeholder={t`Search for a location...`}
            className="bg-soluud-background border-soluud-primary/20 text-soluud-text-primary placeholder:text-soluud-text-primary/40 focus:border-soluud-primary/60 focus:ring-soluud-primary/30 w-full rounded-lg border px-4 py-3 text-sm focus:ring-2 focus:outline-none"
            aria-autocomplete="list"
            aria-controls="location-dropdown"
            aria-expanded={showLocationDropdown}
          />

          {/* Dropdown */}
          {showLocationDropdown && locationQuery && (
            <div
              id="location-dropdown"
              ref={dropdownRef}
              className="bg-soluud-background-card border-soluud-primary/20 absolute top-full left-0 z-10 mt-1 max-h-60 w-full overflow-y-auto rounded-lg border shadow-lg"
              role="listbox"
            >
              {isLoading ? (
                <div className="text-soluud-text-primary/60 flex items-center justify-center px-4 py-8 text-sm">
                  <div className="animate-pulse">
                    <Trans>Searching...</Trans>
                  </div>
                </div>
              ) : locationResults && locationResults.length > 0 ? (
                locationResults.map((location, index) => {
                  const { mainLine, secondaryLine } =
                    formatLocationForDisplay(location);

                  return (
                    <button
                      key={index}
                      onClick={() => handleLocationSelect(location)}
                      className={`hover:bg-soluud-background text-soluud-text-primary flex w-full items-start space-x-3 px-4 py-3 text-left text-sm transition-colors ${
                        selectedIndex === index ? "bg-soluud-background" : ""
                      }`}
                      role="option"
                      aria-selected={selectedIndex === index}
                    >
                      <MapPin className="text-soluud-primary mt-0.5 h-4 w-4 flex-shrink-0" />
                      <div className="min-w-0 flex-1">
                        <div className="truncate font-medium">{mainLine}</div>
                        {secondaryLine && (
                          <div className="text-soluud-text-primary/60 truncate text-xs">
                            {secondaryLine}
                          </div>
                        )}
                      </div>
                    </button>
                  );
                })
              ) : (
                <div className="text-soluud-text-primary/60 flex items-center justify-center px-4 py-8 text-sm">
                  <Trans>No locations found</Trans>
                </div>
              )}
            </div>
          )}
        </div>

        {/* Load Location Button */}
        <button
          type="button"
          onClick={loadLocationFromDevice}
          disabled={locationLookupLoading}
          className="bg-soluud-background-card hover:bg-soluud-background border-soluud-primary/20 hover:border-soluud-primary/60 text-soluud-primary disabled:text-soluud-text-primary/40 disabled:border-soluud-primary/10 flex items-center justify-center rounded-lg border px-4 transition-all disabled:cursor-not-allowed"
          title={t`Use current location`}
          aria-label={t`Use current location`}
        >
          <Locate
            className={`h-5 w-5 ${locationLookupLoading ? "animate-spin" : ""}`}
            aria-hidden="true"
          />
        </button>
      </div>
    </div>
  );
};

export default LocationSearch;
